// ── Colors ──────────────────────────────────────────────────────────────────

export const PURPLE = '#6B21E8'
export const PURPLE_MID = '#8B5CF6'
export const PURPLE_LIGHT = '#EDE9FE'
export const PURPLE_GLOW = 'rgba(107,33,232,0.18)'

// ── Types ───────────────────────────────────────────────────────────────────

export interface EventContext {
  name: string
  guests: number
  city: string
  venueGroupName?: string
  count: number
  privacy: string
  eventType: string
  distance: string
  cityTier: 'premium' | 'essential'
}

export interface VenueSuggestion {
  name: string
  city: string
  type: string
  googlePlaceId?: string
  venueId?: number
}

export interface LocationSuggestion {
  label: string
  city: string | null
}

export interface MyEvent {
  id: number
  name: string
  date: string
  location: string
  guests: number
}

// ── Form Data ───────────────────────────────────────────────────────────────

export const EVENT_TYPES = [
  'Cocktail Reception',
  'Cocktail Reception + Dinner',
  'Dinner',
  'Happy Hour',
  'Holiday Party',
  'Meeting',
  'Meeting with Breakout Spaces',
  'Breakfast',
  'Lunch',
]

export const PRIVACY_OPTIONS = ['Private', 'Semi-Private', 'Buyout', 'Flexible']

export const DISTANCES = ['0.5 mi', '1 mi', '5 mi', '10 mi']

export const SUPPORTED_CITIES: Record<string, { tier: 'premium' | 'essential'; venueCount: number }> = {
  'New York City': { tier: 'premium', venueCount: 4200 },
  'San Francisco Bay Area': { tier: 'premium', venueCount: 2100 },
  'Washington, DC': { tier: 'premium', venueCount: 1800 },
  'Chicago': { tier: 'essential', venueCount: 2830 },
  'Los Angeles': { tier: 'essential', venueCount: 3100 },
  'Miami': { tier: 'essential', venueCount: 1500 },
  'Austin': { tier: 'essential', venueCount: 980 },
  'Boston': { tier: 'essential', venueCount: 1200 },
  'Nashville': { tier: 'essential', venueCount: 870 },
  'Atlanta': { tier: 'essential', venueCount: 1100 },
  'Seattle': { tier: 'essential', venueCount: 950 },
  'Denver': { tier: 'essential', venueCount: 720 },
  'Dallas': { tier: 'essential', venueCount: 890 },
  'Houston': { tier: 'essential', venueCount: 830 },
  'Philadelphia': { tier: 'essential', venueCount: 760 },
  'Las Vegas': { tier: 'essential', venueCount: 1400 },
  'New Orleans': { tier: 'essential', venueCount: 0 },
  'San Diego': { tier: 'essential', venueCount: 0 },
  'Charlottesville': { tier: 'essential', venueCount: 0 },
  'Phoenix': { tier: 'essential', venueCount: 0 },
}

export function getCityTier(cityName: string): 'premium' | 'essential' | null {
  return SUPPORTED_CITIES[cityName]?.tier ?? null
}

export function getCityVenueCount(cityName: string): number {
  return SUPPORTED_CITIES[cityName]?.venueCount ?? 0
}

// ── Venue group boundary validation ─────────────────────────────────────────

type VenueGroupBounds = {
  name?: string
  min_latitude?: number | string | null
  max_latitude?: number | string | null
  min_longitude?: number | string | null
  max_longitude?: number | string | null
}

type NormalizedVenueGroupBounds = {
  name?: string
  minLat: number
  maxLat: number
  minLng: number
  maxLng: number
}

const LOCATION_BOUNDARY_GRACE_MILES = 50

function toFiniteNumber(value: number | string | null | undefined): number | null {
  const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN
  return Number.isFinite(n) ? n : null
}

function normalizeVenueGroupBounds(group: VenueGroupBounds): NormalizedVenueGroupBounds | null {
  const minLat = toFiniteNumber(group.min_latitude)
  const maxLat = toFiniteNumber(group.max_latitude)
  const minLng = toFiniteNumber(group.min_longitude)
  const maxLng = toFiniteNumber(group.max_longitude)
  if (minLat === null || maxLat === null || minLng === null || maxLng === null) return null
  return {
    name: group.name,
    minLat: Math.min(minLat, maxLat),
    maxLat: Math.max(minLat, maxLat),
    minLng: Math.min(minLng, maxLng),
    maxLng: Math.max(minLng, maxLng),
  }
}

function haversineMiles(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 3958.8
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLng = ((lng2 - lng1) * Math.PI) / 180
  const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

function isInsideBounds(lat: number, lng: number, bounds: NormalizedVenueGroupBounds): boolean {
  return lat >= bounds.minLat && lat <= bounds.maxLat && lng >= bounds.minLng && lng <= bounds.maxLng
}

function distanceToBoundsMiles(lat: number, lng: number, bounds: NormalizedVenueGroupBounds): number {
  const nearestLat = clamp(lat, bounds.minLat, bounds.maxLat)
  const nearestLng = clamp(lng, bounds.minLng, bounds.maxLng)
  return haversineMiles(lat, lng, nearestLat, nearestLng)
}

export function getLocationBoundaryValidationError({
  lat,
  lng,
  venueGroups,
}: {
  lat: number | null | undefined
  lng: number | null | undefined
  venueGroups: VenueGroupBounds[]
}): string | null {
  const checkedLat = toFiniteNumber(lat)
  const checkedLng = toFiniteNumber(lng)
  if (checkedLat === null || checkedLng === null) return null

  const bounds = venueGroups.map(normalizeVenueGroupBounds).filter((g): g is NormalizedVenueGroupBounds => Boolean(g))
  if (bounds.length === 0) return null
  if (bounds.some((b) => isInsideBounds(checkedLat, checkedLng, b))) return null

  const nearestDistance = Math.min(...bounds.map((b) => distanceToBoundsMiles(checkedLat, checkedLng, b)))
  if (nearestDistance <= LOCATION_BOUNDARY_GRACE_MILES) return null

  return 'This location looks far outside the cities enabled for your team. Please choose a supported location from the suggestions.'
}

export function getVenueGroupNameForLocation({
  lat,
  lng,
  venueGroups,
}: {
  lat: number | null | undefined
  lng: number | null | undefined
  venueGroups: VenueGroupBounds[]
}): string | null {
  const checkedLat = toFiniteNumber(lat)
  const checkedLng = toFiniteNumber(lng)
  if (checkedLat === null || checkedLng === null) return null

  const matchedGroup = venueGroups.find((group) => {
    const bounds = normalizeVenueGroupBounds(group)
    return bounds ? isInsideBounds(checkedLat, checkedLng, bounds) : false
  })

  return matchedGroup?.name ?? null
}

// ── Place‑type helpers (Google Places) ──────────────────────────────────────

export const AREA_PLACE_TYPES = new Set([
  'locality',
  'sublocality',
  'sublocality_level_1',
  'sublocality_level_2',
  'sublocality_level_3',
  'sublocality_level_4',
  'sublocality_level_5',
  'administrative_area_level_1',
  'administrative_area_level_2',
  'administrative_area_level_3',
  'administrative_area_level_4',
  'administrative_area_level_5',
  'neighborhood',
  'country',
  'postal_code',
  'colloquial_area',
])

export function isAreaPlace(types?: string[]): boolean {
  if (!types || types.length === 0) return false
  return types.some((t) => AREA_PLACE_TYPES.has(t))
}

// Place types that are broader than a city (states, countries, counties)
const BROADER_THAN_CITY_TYPES = new Set([
  'administrative_area_level_1',
  'administrative_area_level_2',
  'country',
])

// City-level or narrower types that override broader types
const CITY_OR_NARROWER_TYPES = new Set([
  'locality',
  'sublocality',
  'sublocality_level_1',
  'sublocality_level_2',
  'sublocality_level_3',
  'sublocality_level_4',
  'sublocality_level_5',
  'neighborhood',
  'postal_code',
])

export function isBroaderThanCity(types?: string[]): boolean {
  if (!types || types.length === 0) return false
  const hasBroad = types.some((t) => BROADER_THAN_CITY_TYPES.has(t))
  const hasCityOrNarrower = types.some((t) => CITY_OR_NARROWER_TYPES.has(t))
  return hasBroad && !hasCityOrNarrower
}

export function detectCityFromPlace(placeDetails: any): string | null {
  if (!placeDetails?.address_components) return null

  const components = placeDetails.address_components as Array<{
    long_name: string
    short_name: string
    types: string[]
  }>

  const locality = components.find((c) => c.types.includes('locality'))?.long_name
  const adminArea = components.find((c) =>
    c.types.includes('administrative_area_level_1'),
  )?.short_name
  const sublocality = components.find((c) => c.types.includes('sublocality'))?.long_name
  const country = components.find((c) => c.types.includes('country'))?.short_name

  if (country && country !== 'US') return null

  const cityName = (locality || sublocality || '').trim()
  if (!cityName) return null

  for (const [supportedCity] of Object.entries(SUPPORTED_CITIES)) {
    const sc = supportedCity.toLowerCase()
    const cn = cityName.toLowerCase()
    const an = (adminArea || '').toLowerCase()

    if (sc.includes(cn) || cn.includes(sc.replace(' city', '').replace(' bay area', ''))) {
      return supportedCity
    }

    if (supportedCity === 'New York City' && (cn === 'new york' || cn === 'brooklyn' || cn === 'manhattan' || cn === 'queens' || cn === 'bronx')) {
      return supportedCity
    }
    if (supportedCity === 'San Francisco Bay Area' && (cn === 'san francisco' || cn === 'oakland' || cn === 'san jose' || cn === 'palo alto')) {
      return supportedCity
    }
    if (supportedCity === 'Washington, DC' && (cn === 'washington' || cn === 'district of columbia') && (an === 'dc' || an === 'district of columbia')) {
      return supportedCity
    }
    if (supportedCity === 'Las Vegas' && cn === 'las vegas') {
      return supportedCity
    }
    if (supportedCity === 'Los Angeles' && (cn === 'los angeles' || cn === 'beverly hills' || cn === 'west hollywood' || cn === 'santa monica')) {
      return supportedCity
    }
  }

  return null
}

export const LOCATION_SUGGESTIONS: LocationSuggestion[] = [
  { label: '375 Park Ave, New York, NY', city: 'New York City' },
  { label: 'Hudson Yards, New York, NY', city: 'New York City' },
  { label: 'Rockefeller Center, New York, NY', city: 'New York City' },
  { label: 'One Market Plaza, San Francisco, CA', city: 'San Francisco Bay Area' },
  { label: 'Union Square, San Francisco, CA', city: 'San Francisco Bay Area' },
  { label: '600 Water St SW, Washington, DC', city: 'Washington, DC' },
  { label: 'Georgetown, Washington, DC', city: 'Washington, DC' },
  { label: 'Millennium Park, Chicago, IL', city: 'Chicago' },
  { label: 'Wacker Drive, Chicago, IL', city: 'Chicago' },
  { label: 'Rodeo Drive, Beverly Hills, CA', city: 'Los Angeles' },
  { label: 'South Beach, Miami, FL', city: 'Miami' },
  { label: '6th Street, Austin, TX', city: 'Austin' },
  { label: 'Back Bay, Boston, MA', city: 'Boston' },
  { label: 'Broadway, Nashville, TN', city: 'Nashville' },
  { label: 'Buckhead, Atlanta, GA', city: 'Atlanta' },
  { label: 'Tokyo, Japan', city: null },
  { label: 'London, UK', city: null },
  { label: 'Paris, France', city: null },
]

export const MY_EVENTS: MyEvent[] = [
  { id: 1, name: 'Q4 Leadership Dinner', date: 'Nov 14, 2025', location: 'New York, NY', guests: 24 },
  { id: 2, name: 'Client Appreciation Reception', date: 'Dec 3, 2025', location: 'San Francisco, CA', guests: 60 },
  { id: 3, name: 'Partner Holiday Dinner', date: 'Dec 18, 2025', location: 'New York, NY', guests: 40 },
  { id: 4, name: 'New Partner Lunch', date: 'Jan 9, 2026', location: 'Chicago, IL', guests: 12 },
  { id: 5, name: 'CMO Summit', date: 'Mar 3, 2026', location: 'Chicago, IL', guests: 120 },
]

// ── Venue Suggestions (mock) ────────────────────────────────────────────────

const VENUE_SUGGESTIONS_MAP: Record<string, VenueSuggestion[]> = {
  per: [
    { name: 'Per Se', city: 'New York, NY', type: 'Fine Dining' },
    { name: 'Perry Street', city: 'New York, NY', type: 'Contemporary' },
  ],
  nobu: [
    { name: 'Nobu Downtown', city: 'New York, NY', type: 'Japanese' },
    { name: 'Nobu Fifty Seven', city: 'New York, NY', type: 'Japanese' },
  ],
  alinea: [{ name: 'Alinea', city: 'Chicago, IL', type: 'Avant-garde' }],
  rpm: [
    { name: 'RPM Italian', city: 'Chicago, IL', type: 'Italian' },
    { name: 'RPM Steak', city: 'Chicago, IL', type: 'Steakhouse' },
  ],
  boka: [{ name: 'Boka', city: 'Chicago, IL', type: 'Contemporary' }],
  spiaggia: [{ name: 'Spiaggia', city: 'Chicago, IL', type: 'Italian' }],
  prime: [{ name: 'Prime & Provisions', city: 'Chicago, IL', type: 'Steakhouse' }],
  le: [
    { name: 'Le Bernardin', city: 'New York, NY', type: 'French' },
    { name: 'Le Pavillon', city: 'New York, NY', type: 'French' },
  ],
  eleven: [{ name: 'Eleven Madison Park', city: 'New York, NY', type: 'Contemporary' }],
  gibsons: [{ name: 'Gibsons Bar & Steakhouse', city: 'Chicago, IL', type: 'Steakhouse' }],
  steak: [
    { name: 'STK Chicago', city: 'Chicago, IL', type: 'Steakhouse' },
    { name: 'Steak 48', city: 'Chicago, IL', type: 'Steakhouse' },
  ],
}

export function getVenueSuggestions(query: string): VenueSuggestion[] {
  if (!query || query.length < 2) return []
  const q = query.toLowerCase()
  for (const key of Object.keys(VENUE_SUGGESTIONS_MAP)) {
    if (q.includes(key) || key.startsWith(q.slice(0, 3))) return VENUE_SUGGESTIONS_MAP[key]
  }
  return [
    { name: `${query} Chicago`, city: 'Chicago, IL', type: 'Restaurant' },
    { name: `${query} New York`, city: 'New York, NY', type: 'Restaurant' },
  ]
}

export function extractChips(brief: string, venues: VenueSuggestion[], context: EventContext): string[] {
  const chips = [`${context.guests} guests`]
  const b = brief.toLowerCase()
  const bm = brief.match(/\$[\d,]+\s*(pp|per person|per head|k\b)?/i)
  if (bm) chips.push(`Budget ${bm[0]}`)
  if (b.includes('rooftop')) chips.push('Rooftop preferred')
  if (b.includes('private') || b.includes('buyout')) chips.push('Full privacy')
  if (b.includes('dinner')) chips.push('Seated dinner')
  if (b.includes('cocktail')) chips.push('Cocktail hour')
  if (b.includes('a/v') || b.includes('av ') || b.includes('projector')) chips.push('A/V required')
  if (b.includes('west loop')) chips.push('West Loop')
  if (b.includes('river north')) chips.push('River North')
  if (b.includes('wine')) chips.push('Strong wine program')
  if (venues.length > 0) chips.push(`${venues[0].name}-caliber`)
  if (chips.length < 3) chips.push(`${context.city} area`)
  return chips.slice(0, 6)
}
